// PATH: src/components/coverage/CoverageMatrixTable.tsx
import * as React from "react";
import { Box, ButtonBase, Card, Tooltip, Typography } from "@mui/material";
import { tokens } from "../../theme/theme";
import type { CoverageItem } from "../../api/coverage/coverage";
import {
  catalogRate,
  catalogTotals,
  cellChapters,
  cellKey,
  cellLessons,
  cellMissing,
  cellState,
  coverageTrackLabel,
  formatPercent,
  gradesTotals,
  subjectTotals,
  type CatalogTotals,
  type CoverageMatrix,
  type MatrixCell,
  type MatrixStage,
} from "./coverage";
import { STATE_LOOK } from "./CoverageSummary";
import type { CellIssues } from "./chapterIssues";

const nf = (n: number) => n.toLocaleString("fr-FR");

const CELL_W = 68;
const SUBJECT_W = 200;

/** Une ligne de la case dans l'infobulle : filière, état, chapitres jouables. */
const rowLine = (r: CoverageItem) => {
  const label = r.in_catalog ? STATE_LOOK[r.state].label : STATE_LOOK.hors_catalogue.label;
  const chapters = r.ossature_chapters > 0 ? ` · ${nf(r.ossature_playable)}/${nf(r.ossature_chapters)} jouables` : "";
  return `${coverageTrackLabel(r)} : ${label.toLowerCase()}${chapters}`;
};

const cellTooltip = (c: MatrixCell, subject: string, grade: string, issues?: CellIssues) => {
  const lessons = cellLessons(c);
  const ch = cellChapters(c);
  const extra = ch.imported - ch.ossature_imported;
  return (
    <Box>
      <b>
        {subject} · {grade}
      </b>
      {c.rows.map((r) => (
        <React.Fragment key={r.track_id ?? "common"}>
          <br />
          {rowLine(r)}
        </React.Fragment>
      ))}
      {extra > 0 && (
        <>
          <br />
          {nf(extra)} importé{extra > 1 ? "s" : ""} hors ossature
        </>
      )}
      {lessons.lessons > 0 && (
        <>
          <br />
          {nf(lessons.ready)}/{nf(lessons.lessons)} leçons prêtes (chapitres importés)
        </>
      )}
      {issues && issues.count > 0 && (
        <>
          <br />
          <b>
            {nf(issues.count)} chapitre{issues.count > 1 ? "s" : ""} à compléter
          </b>
          {issues.severe ? " (dont sans objectifs ou avec une leçon vide)" : ""}
        </>
      )}
      <br />
      <i>Cliquer pour voir les chapitres</i>
    </Box>
  );
};

/** Taux d'une ligne, d'une colonne ou du tableau : « 42 % » puis « 3/7 offres ». */
const RateCell: React.FC<{ totals: CatalogTotals; strong?: boolean }> = ({ totals, strong }) => (
  <>
    <Typography variant="body2" fontWeight={strong ? 700 : 600} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {totals.offers ? formatPercent(catalogRate(totals)) : "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
      {nf(totals.byState.couverte)}/{nf(totals.offers)} offres
    </Typography>
  </>
);

const Cell: React.FC<{
  cell?: MatrixCell;
  subject: string;
  grade: string;
  onOpen: (c: MatrixCell) => void;
  issues?: CellIssues;
  /** Filtre « à compléter seulement » actif et case sans défaut : estompée. */
  dimmed?: boolean;
}> = ({ cell, subject, grade, onOpen, issues, dimmed }) => {
  if (!cell) {
    return (
      <Box sx={{ height: 40, display: "grid", placeItems: "center", color: tokens.borderStrong }} aria-hidden>
        ·
      </Box>
    );
  }
  const state = cellState(cell);
  const look = STATE_LOOK[state];
  const ch = cellChapters(cell);
  const catalogRows = cell.rows.filter((r) => r.in_catalog);
  // Barre du bas : moyenne des offres de la case, comme les taux.
  const p = catalogRows.length ? catalogRate(catalogTotals(catalogRows)) : 0;
  const tracks = cell.rows.filter((r) => r.track_id).length;
  const missing = cellMissing(cell);
  return (
    <Tooltip title={cellTooltip(cell, subject, grade, issues)} disableInteractive>
      <ButtonBase
        onClick={() => onOpen(cell)}
        aria-label={
          `${subject} ${grade} : ${look.label}` +
          (ch.ossature_chapters > 0 ? `, ${ch.ossature_playable} jouables sur ${ch.ossature_chapters}` : "") +
          (missing && state !== "ossature_manquante" ? `, ${missing} filière${missing > 1 ? "s" : ""} sans ossature` : "") +
          (issues?.count ? `, ${issues.count} à compléter` : "")
        }
        sx={{
          opacity: dimmed ? 0.3 : 1,
          position: "relative",
          width: "100%",
          height: 40,
          borderRadius: 1.5,
          bgcolor: look.bg,
          color: look.fg,
          border: `1px dashed ${state === "hors_catalogue" ? tokens.border : "transparent"}`,
          fontSize: "0.8125rem",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          overflow: "hidden",
          transition: "box-shadow .12s, transform .12s",
          "&:hover": { boxShadow: `0 0 0 2px ${tokens.borderStrong}` },
          "&:focus-visible": { boxShadow: "0 0 0 2px #444CE7" },
        }}
      >
        {state === "ossature_manquante" ? (
          <Box component="span" sx={{ fontSize: "0.6875rem", fontWeight: 700 }}>
            Sans oss.
          </Box>
        ) : state === "hors_catalogue" ? (
          <span title="Hors catalogue">{ch.imported}*</span>
        ) : (
          <span>
            {ch.ossature_playable}
            <Box component="span" sx={{ opacity: 0.55, fontWeight: 500 }}>
              /{ch.ossature_chapters}
            </Box>
          </span>
        )}
        {issues && issues.count > 0 && (
          <Box
            component="span"
            aria-hidden
            sx={{
              position: "absolute",
              top: 2,
              left: 2,
              minWidth: 14,
              height: 14,
              px: "3px",
              borderRadius: 99,
              bgcolor: issues.severe ? "error.main" : "warning.main",
              color: "#fff",
              fontSize: "0.5625rem",
              fontWeight: 700,
              lineHeight: "14px",
              textAlign: "center",
            }}
          >
            {issues.count}
          </Box>
        )}
        {tracks > 1 && (
          <Box
            component="span"
            sx={{ position: "absolute", top: 2, right: 4, fontSize: "0.5625rem", fontWeight: 700, opacity: 0.6 }}
          >
            {tracks}F
          </Box>
        )}
        {missing > 0 && state !== "ossature_manquante" && (
          // Une filière sans ossature dans une case mélangée : jamais masquée.
          <Box
            component="span"
            aria-hidden
            sx={{ position: "absolute", right: 3, bottom: 5, width: 7, height: 7, borderRadius: 99, bgcolor: tokens.coverage.missing }}
          />
        )}
        {p > 0 && (
          <Box sx={{ position: "absolute", left: 0, bottom: 0, height: 3, width: `${Math.max(p * 100, 8)}%`, bgcolor: tokens.coverage.playable }} />
        )}
      </ButtonBase>
    </Tooltip>
  );
};

interface Props {
  matrix: CoverageMatrix;
  stages: MatrixStage[];
  subjectFilter: (name: string) => boolean;
  onOpenCell: (cell: MatrixCell) => void;
  /** Chapitres à compléter par case (clé `cellKey`) ; absent = non évalué. */
  cellIssues?: Map<string, CellIssues> | null;
  /** N'accentuer que les cases qui ont des chapitres à compléter. */
  onlyIncomplete?: boolean;
}

/** Matrice matière × niveau, niveaux groupés par étape. En-têtes et 1re colonne collants. */
export const CoverageMatrixTable: React.FC<Props> = ({
  matrix,
  stages,
  subjectFilter,
  onOpenCell,
  cellIssues,
  onlyIncomplete,
}) => {
  const grades = stages.flatMap((s) => s.grades);
  const gradeIds = grades.map((g) => g.id);
  const subjects = matrix.subjects.filter(
    (s) => subjectFilter(s.name) && gradeIds.some((g) => matrix.cells.has(cellKey(s.id, g))),
  );

  const stickyLeft = {
    position: "sticky" as const,
    left: 0,
    zIndex: 2,
    bgcolor: "background.paper",
    borderRight: `1px solid ${tokens.border}`,
  };

  if (!subjects.length) {
    return (
      <Card sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">Aucune matière ne correspond.</Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ overflow: "hidden" }}>
      <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 220px)" }}>
        <Box
          component="table"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: "100%",
            "& td, & th": { p: 0.5, borderBottom: `1px solid ${tokens.border}` },
            "& tbody tr:last-of-type td": { borderBottom: 0 },
          }}
        >
          <Box component="thead" sx={{ position: "sticky", top: 0, zIndex: 3, bgcolor: tokens.surfaceMuted }}>
            <tr>
              <Box component="th" rowSpan={2} sx={{ ...stickyLeft, zIndex: 4, bgcolor: tokens.surfaceMuted, minWidth: SUBJECT_W, textAlign: "left", px: "16px !important" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Matière
                </Typography>
              </Box>
              {stages.map((st, i) => (
                <Box
                  component="th"
                  key={st.id || i}
                  colSpan={st.grades.length}
                  sx={{
                    textAlign: "left",
                    px: "10px !important",
                    pt: "10px !important",
                    bgcolor: tokens.surfaceMuted,
                    borderLeft: i ? `1px solid ${tokens.border}` : undefined,
                    borderBottom: "0 !important",
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    {st.name}
                  </Typography>
                </Box>
              ))}
              <Box component="th" rowSpan={2} sx={{ bgcolor: tokens.surfaceMuted, minWidth: 92, borderLeft: `1px solid ${tokens.border}` }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total
                </Typography>
              </Box>
            </tr>
            <tr>
              {stages.flatMap((st, i) =>
                st.grades.map((g, j) => (
                  <Box
                    component="th"
                    key={g.id}
                    sx={{
                      minWidth: CELL_W,
                      width: CELL_W,
                      bgcolor: tokens.surfaceMuted,
                      borderLeft: i && j === 0 ? `1px solid ${tokens.border}` : undefined,
                    }}
                  >
                    <Tooltip title={g.name}>
                      <Typography variant="caption" fontWeight={650} color="text.primary">
                        {g.code}
                      </Typography>
                    </Tooltip>
                  </Box>
                )),
              )}
            </tr>
          </Box>
          <tbody>
            {subjects.map((s) => {
              const t = subjectTotals(matrix, s.id, gradeIds);
              return (
                <Box component="tr" key={s.id} sx={{ "&:hover td": { bgcolor: tokens.surfaceMuted } }}>
                  <Box component="td" sx={{ ...stickyLeft, px: "16px !important" }}>
                    <Typography variant="body2" fontWeight={550} noWrap sx={{ maxWidth: SUBJECT_W - 32 }} title={s.name}>
                      {s.name}
                    </Typography>
                  </Box>
                  {stages.flatMap((st, i) =>
                    st.grades.map((g, j) => (
                      <Box component="td" key={g.id} sx={{ borderLeft: i && j === 0 ? `1px solid ${tokens.border}` : undefined }}>
                        <Cell
                          cell={matrix.cells.get(cellKey(s.id, g.id))}
                          subject={s.name}
                          grade={g.name}
                          onOpen={onOpenCell}
                          issues={cellIssues?.get(cellKey(s.id, g.id))}
                          dimmed={Boolean(onlyIncomplete && cellIssues && !cellIssues.get(cellKey(s.id, g.id)))}
                        />
                      </Box>
                    )),
                  )}
                  <Box component="td" sx={{ borderLeft: `1px solid ${tokens.border}`, px: "12px !important", textAlign: "right", whiteSpace: "nowrap" }}>
                    <RateCell totals={t} />
                  </Box>
                </Box>
              );
            })}
            {/* Taux par niveau : toutes les matières offertes, filtre de recherche ignoré. */}
            <Box component="tr" sx={{ "& td": { bgcolor: tokens.surfaceMuted, borderTop: `1px solid ${tokens.borderStrong}` } }}>
              <Box component="td" sx={{ ...stickyLeft, bgcolor: tokens.surfaceMuted, px: "16px !important" }}>
                <Typography variant="body2" fontWeight={650}>
                  Couverture du niveau
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  toutes matières offertes
                </Typography>
              </Box>
              {stages.flatMap((st, i) =>
                st.grades.map((g, j) => (
                  <Box
                    component="td"
                    key={g.id}
                    sx={{ textAlign: "center", borderLeft: i && j === 0 ? `1px solid ${tokens.border}` : undefined }}
                  >
                    <RateCell totals={gradesTotals(matrix, [g.id])} />
                  </Box>
                )),
              )}
              <Box component="td" sx={{ borderLeft: `1px solid ${tokens.border}`, px: "12px !important", textAlign: "right" }}>
                <RateCell totals={gradesTotals(matrix, gradeIds)} strong />
              </Box>
            </Box>
          </tbody>
        </Box>
      </Box>
    </Card>
  );
};

export default CoverageMatrixTable;
